import Head from 'next/head';
import {useRouter} from 'next/router';

import {HeadSEO} from '@udemy/head-seo';
import {useI18n} from '@udemy/i18n';
import {SubscriptionPlansProvider, SubscriptionPlanProductType} from '@udemy/subscription-graphql';

import {useSetupNavigation} from 'src/hooks/use-setup-navigation';
import {useCanonicalUrl} from 'src/hooks/useCanonicalUrl';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {getOgLocale} from 'src/lib/get-og-locale';
import {PageProps} from 'src/types/page-props';

import {GoogleAiPathPage, GoogleAiPathPageProps} from './google-ai-path-page.react-component';

export type GoogleAiPathPageFeatureProps = PageProps & GoogleAiPathPageProps;

export function GoogleAiPathPageFeature(props: GoogleAiPathPageFeatureProps) {
    const router = useRouter();
    const {interpolate, gettext} = useI18n();
    const {url: canonicalUrl} = useCanonicalUrl([], META_DEFAULT_BASE_URL);
    useSetupNavigation();
    const metaProps = {
        title: interpolate(
            gettext('%(careerName)s | Udemy'),
            {careerName: props.accelerator.career.name},
            true,
        ),
        description: props.accelerator.seoDescription ?? props.accelerator.primaryDescription,
    };

    const ogLocale = getOgLocale(props.locale);

    return (
        <>
            <HeadSEO
                title={metaProps.title}
                ogTitle={metaProps.title}
                description={metaProps.description}
                ogDescription={metaProps.description}
                ogLocale={ogLocale}
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <SubscriptionPlansProvider
                subscriptionPlanQueryVariables={[
                    {
                        filters: {
                            productType: SubscriptionPlanProductType.Consumersubscription,
                        },
                        marketingURL: `${new URL(canonicalUrl).origin}${router?.asPath}`,
                    },
                    {
                        filters: {
                            productType: SubscriptionPlanProductType.GrowWithGoogle,
                        },
                        marketingURL: `${new URL(canonicalUrl).origin}${router?.asPath}`,
                    },
                    {
                        filters: {
                            productType:
                                SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
                        },
                        marketingURL: `${new URL(canonicalUrl).origin}${router?.asPath}`,
                    },
                ]}
            >
                <GoogleAiPathPage
                    accelerator={props.accelerator}
                    instructor={props.instructor}
                    topics={props.topics}
                    isEnrolled={props.isEnrolled}
                    allGwgAccelerators={props.allGwgAccelerators}
                />
            </SubscriptionPlansProvider>
        </>
    );
}
